<template>
  <lazy-component
    wrapper-tag="svg"
    :viewBox="viewBox"
    :class="{ 'with-border': withBorder }"
    class="d-block case-thumbnail"
    v-bind="{ ...$attrs }"
    :data-cy="dataCy"
  >
    <rect x="0" y="0" :width="size" :height="size" class="backdrop" />
    <thumbnail-image
      v-if="showImage"
      v-show="!isFailed"
      ref="image"
      inline-svg
      :thumbnail="thumbnail"
      v-bind="{ ...dimension, ...position }"
    />
    <annotation-layer v-if="showImage" :reference-image="thumbnail" v-bind="{ ...dimension, ...position }" />
    <foreignObject v-if="!showImage" x="0" y="0" :width="size" :height="size">
      <center-box>
        <small v-if="!thumbnail && !!noImageMessage" class="text-center m-4">{{ noImageMessage }}</small>
        <i v-if="(!thumbnail && !noImageMessage) || isFailed" class="fa fa-question-circle text-muted"></i>
        <i v-if="!!thumbnail && !thumbnail.isSupported" class="fa fa-eye-slash text-muted"></i>
      </center-box>
    </foreignObject>
  </lazy-component>
</template>

<script>
  import { ThumbnailResource } from '@/api-resources';
  import ThumbnailImage from '@/Atoms/ThumbnailImage';
  import AnnotationLayer from '@/Molecules/AnnotationLayer';
  import CenterBox from '@/Atoms/CenterBox';
  import { THUMBNAIL_SIZE } from '@/config';

  export default {
    name: 'ThumbnailSvg',

    components: {
      ThumbnailImage,
      AnnotationLayer,
      CenterBox,
    },

    props: {
      thumbnail: ThumbnailResource,
      withBorder: Boolean,
      noImageMessage: String,
      dataCy: String,
      size: {
        type: Number,
        default: THUMBNAIL_SIZE,
      },
    },

    data() {
      return {
        isFailed: false,
      };
    },

    computed: {
      dimension() {
        if (!this.thumbnail) {
          return { width: this.size, height: this.size };
        }

        let originalWidth = this.thumbnail.originalWidth;
        if (originalWidth === 0) {
          originalWidth = this.size;
        }

        let originalHeight = this.thumbnail.originalHeight;
        if (originalHeight === 0) {
          originalHeight = this.size;
        }

        const scale = Math.max(originalWidth, originalHeight) / this.size;
        const width = Math.min(this.size, Math.round(originalWidth / scale) || this.size);
        const height = Math.min(this.size, Math.round(originalHeight / scale) || this.size);

        return { width, height };
      },

      position() {
        const x = Math.round((this.size - this.dimension.width) / 2);
        const y = Math.round((this.size - this.dimension.height) / 2);

        return { x, y };
      },

      viewBox() {
        const size = this.size ?? Math.max(this.dimension.width, this.dimension.height);

        return `0 0 ${size} ${size}`;
      },

      showImage() {
        return !!this.thumbnail && this.thumbnail.isSupported && !this.isFailed;
      },
    },

    methods: {
      onError() {
        this.isFailed = true;
        this.$emit('error');
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../sass/config/variables';

  .backdrop {
    fill: $black;
  }

  .with-border {
    border: 1px solid $gray-900;

    a:focus &,
    a.selected & {
      border-color: $gray-50;
    }
  }
</style>
